import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { LeftSideNavContainer } from '../../components/page-containers/LeftSideNavContainer';
import {
  SideNav,
  SideNavCategory,
  SideNavItemSelectEvent,
  SideNavCategoryTypeName,
} from '../../components/side-nav';
import ReportInfo from './report-info/ReportInfo';
import {
  fetchCustomCalendarWithCache,
  fetchReportSnapshot,
  fetchReportTypeSettingWithCache,
  fetchSavedReport,
  fetchSharedReport,
  RecentReport,
  SavedReport,
} from '../../api';
import {
  StandardReport,
  ReportSnapshot,
  ClientCustomCalendarModel,
  StringOrComponent,
  SavedReportDetail,
  ReportMetadata,
  CreatedFromReportMetadata,
} from '../../models';
import {
  findSelectedCategoryItemsFromReport,
  runStandardReport,
  checkIfHasEditingRuleBuilderField,
  combineWithSearchableCategories,
  buildSharedWithMeCategory,
  buildCustomReportTag,
  emptyReportTag,
  objectsAreEqual,
} from './helpers';
import * as Styled from './styled';
import * as GStyled from '../../styles/components';
import { ButtonsBar } from '../../styles/components';
import { Button } from '@leasequery/lq.react-component-library';
import ReportSetting from './report-setting/ReportSetting';
import { ReportType } from '../../models';
import { useAuth, useInprogressReports } from '../../context';

import {
  ReportSettingState,
  initialEmptyReportSettingState,
  initialReportSettingState,
} from './report-setting';
import StandardReportIncludedFields from './standard-report-included-fields/StandardReportIncludedFields';
import { useRouter } from 'next/router';
import { fetchStandardReportCategoriesWithCache } from './helpers/fetchStandardReportCategoriesWithCache';
import { createReportSettingStateFromReportSnapshot } from './report-setting/helpers/createReportSettingStateFromReportSnapshot';
import { RuleBuilderField } from '../create-report/rule-builder';
import { buildMyReportCategory } from './helpers/buildMyReportCategory';
import { FindReportSideNavItem, FindReportSideNavItemTag } from '.';
import withClient from '../../components/with-client/withClient';
import { NoItemsToDisplay } from '../../components/side-nav/sub-category-section/NoItemsToDisplay';
import {
  ReportNotifications,
  ActionMenu,
  Dialog,
  Icon,
  ActionMenuCompoundedComponent,
} from '../../components';
import { formatToLocalTime, isEmptyObject, noop } from '../../util';
import { createReportSettingStateFromSavedReport } from './report-setting/helpers/createReportSettingStateFromSavedReport';
import ReportDateSetting from './report-setting/report-date-setting/ReportDateSetting';
import { getStandardReportNameById } from './report-setting/helpers/getStandardReportNameById';
import { updateReportSnapshotForCompleteDisclosureReports } from './report-setting/helpers/buildClientAllocationOptions';
import { useReportContext } from '../../context/ReportContext';
import { replaceIfrsLabels } from '../../util/frs102Helper';
import { useIsFrs102Enabled } from '../../util/hooks/useIsFrs102Enabled';
import { useRunReportErrorHandler } from '../../util/hooks/useRunReportErrorHandler';
import { HybridReportData } from '../../models';
import { ReportName } from '../../constants/reportSetting';

function FindReport(): JSX.Element {
  const router = useRouter();
  const { user, client, clientSetting } = useAuth();
  const clientId = client ? client.clientId : user.clientId;
  const isFrs102Enabled = useIsFrs102Enabled();

  const [, { addNewInProgressReport }] = useInprogressReports();

  const [isShowNotification, setIsShowNotification] = useState(false);

  const [
    isShowCreateReportErrorNotification,
    setIsShowCreateReportErrorNotification,
  ] = useState(false);
  const onApiError = useCallback((): void => {
    setIsShowNotification(false);
    setIsShowCreateReportErrorNotification(true);
  }, []);
  const { errorMessage: runReportErrorMessage, resetError } =
    useRunReportErrorHandler(onApiError);

  const [isInforReport, setIsInforReport] = useState(false);
  const [isCustomAPReport, setIsCustomAPReport] = useState(false);

  const [categories, setCategories] = useState<Array<FindReportSideNavItem>>(
    [],
  );
  const [searchableCategories, setSearchableCategories] = useState<
    Array<SideNavCategory>
  >([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  const [selectedCategory, setSelectedCategory] =
    useState<FindReportSideNavItem>(null);
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<FindReportSideNavItem>();
  // current selection used for non-standard reports ex My Reports and Shared With Me categories
  const [selectedNonStandardCategory, setSelectedNonStandardCategory] =
    useState<FindReportSideNavItem>(null);
  const [selectedNonStandardSubCategory, setSelectedNonStandardSubCategory] =
    useState<FindReportSideNavItem>();
  const [isActionButtonsEnabled, setEnableActionButtons] =
    useState<boolean>(true);
  const [reportSnapshot, setReportSnapshot] = useState<ReportSnapshot>(null);
  const [savedReport, setSavedReport] = useState<SavedReportDetail>(null);
  const [sharedReport, setSharedReport] = useState<SavedReportDetail>(null);
  const [reportMetadata, setReportMetadata] = useState<ReportMetadata>();

  const [customCalendarSnapshot, setCustomCalendarSnapshot] =
    useState<ClientCustomCalendarModel>(null);
  const [reportTypeSetting, setReportTypeSetting] = useState(null);
  const [ruleBuilderFields, setRuleBuilderFields] = useState<
    RuleBuilderField[]
  >([]);

  const [defaultReportSettingState, setDefaultReportSettingState] = useState(
    initialEmptyReportSettingState(),
  );

  const actionMenuRef = useRef<ActionMenuCompoundedComponent>();

  const {
    setAutoSelectScrollTo,
    reportSettingState,
    setReportSettingState,
    scheduledReportSettingState,
    setScheduledReportSettingState,
    shouldSaveAndContinue,
    setHybridReportTransferData,
  } = useReportContext();

  const autoSubcategorySelectHandler = async (
    selectedReportId: number,
  ): Promise<void> => {
    await router.replace('/find', '/find', { shallow: true });

    const report = findSelectedCategoryItemsFromReport(
      selectedReportId,
      categories[0].items as FindReportSideNavItem[],
    );

    setSelectedCategory(report.selectedCategory);
    setSelectedSubCategory(report.selectedSubCategory);

    const { reportType } = report.selectedCategory.tag as StandardReport;
    const initialReportTypeSetting = await fetchReportTypeSettingWithCache(
      clientId,
    );

    setReportSnapshot(null);
    setSelectedNonStandardCategory(null);
    setSelectedNonStandardSubCategory(null);
    setReportMetadata(null);
    setAutoSelectScrollTo(true);

    const reportSettingInitialState = initialReportSettingState(
      initialReportTypeSetting,
      reportType as ReportType,
      isInforReport,
      isCustomAPReport,
    );
    setDefaultReportSettingState(reportSettingInitialState);
    setReportSettingState(reportSettingInitialState);
  };

  //#region Unsaved Changes
  const [initialSettingsToCompare, setInitialSettingsToCompare] =
    useState(null);
  const [reportHasUnsavedChanges, setReportHasUnsavedChanges] = useState(false);
  const [fieldsHaveUnsavedChanges, setFieldsHaveUnsavedChanges] =
    useState(false);
  const [settingsHaveUnsavedChanges, setSettingsHaveUnsavedChanges] =
    useState(false);

  useEffect(() => {
    setReportHasUnsavedChanges(
      fieldsHaveUnsavedChanges || settingsHaveUnsavedChanges,
    );
  }, [fieldsHaveUnsavedChanges, settingsHaveUnsavedChanges]);

  const resetUnsavedState = useCallback(() => {
    setFieldsHaveUnsavedChanges(false);
    setSettingsHaveUnsavedChanges(false);
  }, []);

  //#endregion

  // report metadata loading
  useEffect(() => {
    const getOriginalReportDetails = (): CreatedFromReportMetadata => {
      let standardReportId: number = null;

      if (savedReport) {
        standardReportId = savedReport.reportSetting.standardReportId;
      }

      if (reportSnapshot) {
        standardReportId = reportSnapshot.standardReportId;
      }

      if (!standardReportId) {
        return;
      }

      const standardReportName = getStandardReportNameById(
        standardReportId,
        searchableCategories as Array<SideNavCategory<StandardReport>>,
      );

      return { reportName: standardReportName, reportId: standardReportId };
    };
    if (searchableCategories.length !== 0) {
      if (savedReport) {
        const { createdByUserId, createdDate, updatedDate } = savedReport;

        const neverUpdated = updatedDate === '0001-01-01T00:00:00';

        const dateToUse = neverUpdated ? createdDate : updatedDate;

        // format the date
        const formattedCreatedDate = formatToLocalTime(new Date(dateToUse));

        // get original report info
        const originalReportDetails = getOriginalReportDetails();

        setReportMetadata({
          createdByUserId,
          lastUpdated: formattedCreatedDate,
          createdFromReport: originalReportDetails,
        });
      }
    }
  }, [savedReport, searchableCategories, reportSnapshot]);

  // toggle to allow My Reports to be updated
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const updateMyReports = useCallback(() => {
    setShouldUpdate((curr) => !curr);
  }, []);

  // load standard, shared, and my reports categories
  useEffect(() => {
    const handleGetStandardReports = async (): Promise<void> => {
      const [
        reportCategory,
        initialReportTypeSetting,
        myReportCategory,
        sharedWithMeCategory,
        customCalendarResponse,
      ] = await Promise.all([
        fetchStandardReportCategoriesWithCache(clientId),
        fetchReportTypeSettingWithCache(clientId),
        buildMyReportCategory(clientId),
        buildSharedWithMeCategory(clientId),
        fetchCustomCalendarWithCache(clientId),
      ]);

      reportCategory.items.unshift(sharedWithMeCategory);
      reportCategory.items.unshift(myReportCategory);
      setCustomCalendarSnapshot(customCalendarResponse);
      setCategories([reportCategory]);
      setReportTypeSetting(initialReportTypeSetting);
      setIsLoadingCategories(false);
      setSearchableCategories(
        combineWithSearchableCategories(
          reportCategory,
          myReportCategory,
          sharedWithMeCategory,
        ),
      );
    };

    handleGetStandardReports();
  }, [clientId, shouldUpdate]);

  useEffect(() => {
    // default to My Reports on first render
    if (selectedCategory === null && categories[0]) {
      setSelectedCategory(categories[0].items[0] as FindReportSideNavItem);
      setIsLoadingCategories(false);
    }
  }, [categories, selectedCategory]);

  // the actual displayed current selected report
  // selectedSubCategory for reports that have a standard report id ex all report templates
  // selectedNonStandardSubCategory for my reports/shared with me and all custom reports
  const currentSelectedReport = useMemo(() => {
    return selectedNonStandardSubCategory ?? selectedSubCategory;
  }, [selectedNonStandardSubCategory, selectedSubCategory]);

  // load report data from query params
  useEffect(() => {
    // reset existing report data
    setReportSnapshot(null);
    setSavedReport(null);
    setSharedReport(null);
    setSelectedSubCategory(null);

    // if no params nothing to load
    if (isEmptyObject(router.query)) {
      return;
    }

    const {
      reportSnapshotId,
      savedReportId,
      sharedReportId,
      expandSavedReports,
      expandSharedReports,
    } = router.query;

    // force selection if user clicks See All from landing page
    if ((expandSharedReports || expandSavedReports) && categories) {
      const forceSelectCategory = expandSavedReports
        ? categories[0]?.items[0]
        : categories[0]?.items[1];
      setSelectedCategory(forceSelectCategory as FindReportSideNavItem);
      return;
    }

    const handleGetPreviousReports = async (): Promise<void> => {
      // Fetch all dependencies data
      const [
        reportCategory,
        initialReportSnapshot,
        initialReportTypeSetting,
        initialSavedReport,
        initialSharedReport,
        customCalendarResponse,
      ] = await Promise.all([
        fetchStandardReportCategoriesWithCache(clientId),
        reportSnapshotId
          ? fetchReportSnapshot(parseInt(reportSnapshotId as string))
          : null,
        fetchReportTypeSettingWithCache(clientId),
        savedReportId
          ? fetchSavedReport(parseInt(savedReportId as string))
          : null,
        sharedReportId
          ? fetchSharedReport(parseInt(sharedReportId as string), clientId)
          : null,
        fetchCustomCalendarWithCache(clientId),
      ]);

      setCustomCalendarSnapshot(customCalendarResponse);

      let subCategoryToSelect = null;
      let reportSnapshot = initialReportSnapshot;
      let savedReport = initialSavedReport;

      // if there is a reportSnapshot specified
      // load default data from this snapshot
      if (initialReportSnapshot) {
        if (
          initialReportSnapshot.standardReportTypeDisplay ===
            ReportType.disclosure ||
          (initialReportSnapshot.standardReportTypeDisplay === ReportType.ap &&
            initialReportSnapshot.reportName === 'AP Report')
        ) {
          reportSnapshot = updateReportSnapshotForCompleteDisclosureReports(
            initialReportSnapshot,
            initialReportTypeSetting,
          );
        }
        setReportSnapshot(reportSnapshot);

        // Select the snapshot's standard report form side nav
        const searchResult = findSelectedCategoryItemsFromReport(
          reportSnapshot.standardReportId,
          reportCategory.items as FindReportSideNavItem[],
        );
        if (searchResult) {
          subCategoryToSelect = searchResult.selectedSubCategory;

          const builtCustomReportSubCategoryItem: FindReportSideNavItem = {
            name: reportSnapshot.reportName,
            key: reportSnapshotId as string,
            tag: emptyReportTag,
            parent: categories[0]?.items[0].items[0],
          };

          // rebuild the selected report
          if (subCategoryToSelect) {
            subCategoryToSelect.key = reportSnapshotId;
            subCategoryToSelect.name = reportSnapshot.reportName;
            setIsInforReport(
              subCategoryToSelect.tag.reportType === 'AP' &&
                subCategoryToSelect.tag.runType !== 'ap',
            );
          }

          // set category to My Reports
          setSelectedNonStandardCategory(
            categories[0]?.items[0] as FindReportSideNavItem,
          );
          setSelectedNonStandardSubCategory(
            subCategoryToSelect ?? builtCustomReportSubCategoryItem,
          );
        }

        // if recent report was previously saved, use that saved name when running the report
        if (reportSnapshot.standardReportId && subCategoryToSelect) {
          subCategoryToSelect.tag.name = reportSnapshot.reportName;
        }

        // fill up the reportsettings if settings from snapshot
        const reportSettingStateFromSnapshot =
          createReportSettingStateFromReportSnapshot(
            reportSnapshot,
            initialReportTypeSetting,
          );
        if (reportSettingStateFromSnapshot) {
          setReportSettingState(reportSettingStateFromSnapshot);
          setDefaultReportSettingState(reportSettingStateFromSnapshot);
        }
      } else if (initialSavedReport) {
        const searchResult = findSelectedCategoryItemsFromReport(
          initialSavedReport.reportSetting?.standardReportId,
          reportCategory.items as FindReportSideNavItem[],
        );
        if (searchResult) {
          subCategoryToSelect = searchResult.selectedSubCategory;

          const builtCustomItemToSelect: FindReportSideNavItem = {
            name: initialSavedReport.reportName,
            key: savedReportId as string,
            tag: emptyReportTag,
            parent: categories[0]?.items[0].items[1],
          };

          // rebuild the selected report
          if (subCategoryToSelect) {
            subCategoryToSelect.key = savedReportId;
            subCategoryToSelect.name = initialSavedReport.reportName;
          }

          // set category to My Reports
          setSelectedNonStandardCategory(
            categories[0]?.items[0] as FindReportSideNavItem,
          );
          setSelectedNonStandardSubCategory(
            subCategoryToSelect ?? builtCustomItemToSelect,
          );
        }

        const standardReport = subCategoryToSelect?.tag as StandardReport;
        const reportType = standardReport?.reportType as ReportType;
        if (reportType === ReportType.disclosure) {
          savedReport = {
            ...initialSavedReport,
            reportIncludedFields: [],
          };
        }
        setSavedReport(savedReport);

        const reportSettingStateFromSavedReport =
          createReportSettingStateFromSavedReport(
            clientId,
            initialSavedReport,
            standardReport,
            initialReportTypeSetting,
          );
        if (reportSettingStateFromSavedReport) {
          setReportSettingState(reportSettingStateFromSavedReport);
          setDefaultReportSettingState(reportSettingStateFromSavedReport);

          // capture initial state for unsaved change comparison
          setInitialSettingsToCompare(reportSettingStateFromSavedReport);
        }
      } else if (initialSharedReport) {
        setSharedReport(initialSharedReport);

        const searchResult = findSelectedCategoryItemsFromReport(
          initialSharedReport.reportSetting?.standardReportId,
          reportCategory.items as FindReportSideNavItem[],
        );
        if (searchResult) {
          subCategoryToSelect = searchResult.selectedSubCategory;

          const builtCustomItemToSelect: FindReportSideNavItem = {
            name: initialSharedReport.reportName,
            key: sharedReportId as string,
            tag: emptyReportTag,
            parent: categories[0]?.items[1].items[0],
          };

          // rebuild the selected report
          if (subCategoryToSelect) {
            subCategoryToSelect.key = sharedReportId;
            subCategoryToSelect.name = initialSharedReport.reportName;
          }

          // set category to My Reports
          setSelectedNonStandardCategory(
            categories[0]?.items[1] as FindReportSideNavItem,
          );
          setSelectedNonStandardSubCategory(
            subCategoryToSelect ?? builtCustomItemToSelect,
          );
        }

        const reportSettingStateFromSharedReport =
          createReportSettingStateFromSavedReport(
            clientId,
            initialSharedReport,
            subCategoryToSelect?.tag as StandardReport,
            initialReportTypeSetting,
          );
        if (reportSettingStateFromSharedReport) {
          setReportSettingState(reportSettingStateFromSharedReport);
          setDefaultReportSettingState(reportSettingStateFromSharedReport);

          // capture initial state for unsaved change comparison
          setInitialSettingsToCompare(reportSettingStateFromSharedReport);
        }
      }

      setSelectedSubCategory(subCategoryToSelect);
    };

    handleGetPreviousReports();
  }, [router.query, clientId, categories, setReportSettingState]);

  const handleRouterPush = useCallback(
    async (reportId: number, path: string): Promise<void> => {
      router.push(`/find?${path}=${reportId}`);
    },
    [router],
  );

  const handleMyReportSelection = useCallback(
    (
      category: 'MyReports' | 'SharedWithMe',
      selectedReport: FindReportSideNavItem,
      reportDescription?: string,
    ): void => {
      const categoryToSelect =
        category === 'MyReports'
          ? categories[0].items[0]
          : categories[0].items[1];

      selectedReport.tag = buildCustomReportTag(
        selectedReport,
        reportDescription,
      );

      setSelectedNonStandardCategory(categoryToSelect as FindReportSideNavItem);
      setSelectedNonStandardSubCategory(selectedReport);
    },
    [categories],
  );

  const subCategorySelectHandler = useCallback(
    async (e: SideNavItemSelectEvent<FindReportSideNavItemTag>) => {
      // reset existing report data
      setReportMetadata(null);
      setSavedReport(null);
      setSharedReport(null);
      setReportHasUnsavedChanges(false);
      setFieldsHaveUnsavedChanges(false);
      setSettingsHaveUnsavedChanges(false);

      switch (e.category.typeName) {
        case SideNavCategoryTypeName.RecentReport: {
          const reportId = (e.category.tag as RecentReport).taskId;

          handleMyReportSelection('MyReports', e.category);
          handleRouterPush(reportId, 'reportSnapshotId');
          break;
        }
        case SideNavCategoryTypeName.SavedReport: {
          const reportId = (e.category.tag as SavedReport).id;

          handleMyReportSelection('MyReports', e.category);
          handleRouterPush(reportId, 'savedReportId');
          break;
        }
        case SideNavCategoryTypeName.SharedReport:
          const reportId = (e.category.tag as SavedReport).id;

          handleMyReportSelection('SharedWithMe', e.category);
          handleRouterPush(reportId, 'sharedReportId');
          break;
        default:
          const standardReportClickEvent =
            e as SideNavItemSelectEvent<StandardReport>;
          const { reportType } = standardReportClickEvent.category.tag;
          setReportSnapshot(null);
          setIsInforReport(
            standardReportClickEvent.category.key === 'Infor AP Report',
          );

          const isCustomAPReport =
            standardReportClickEvent.category.key === ReportName.CustomAP;
          setIsCustomAPReport(isCustomAPReport);

          setSelectedSubCategory(standardReportClickEvent.category);

          const reportSettingState = initialReportSettingState(
            reportTypeSetting,
            reportType as ReportType,
            standardReportClickEvent.category.key === 'Infor AP Report',
            isCustomAPReport,
          );
          setDefaultReportSettingState(reportSettingState);
          setReportSettingState(reportSettingState);

          // capture initial state for unsaved change comparison
          setInitialSettingsToCompare(reportSettingState);

          // clear query params if navigating from a saved/recent report to standard
          if (!isEmptyObject(router.query)) {
            router.replace('/find', '/find', { shallow: true });
          }
      }
    },
    [
      handleMyReportSelection,
      handleRouterPush,
      reportTypeSetting,
      setReportSettingState,
      router,
    ],
  );

  const categorySelectHandler = useCallback(
    (e: SideNavItemSelectEvent<FindReportSideNavItemTag>) => {
      setSelectedCategory(e.category);
      setSelectedSubCategory(null);

      setSelectedNonStandardCategory(null);
      setSelectedNonStandardSubCategory(null);

      // clear query params if navigating from a saved/recent report to standard
      router.replace('/find', '/find', { shallow: true });

      // when changing between categories clear setting state
      setReportSettingState(initialEmptyReportSettingState());
    },
    [router, setReportSettingState],
  );

  useEffect(() => {
    const handleGetReportTypeSetting = async (): Promise<void> => {
      const reportTypeSettingResult = await fetchReportTypeSettingWithCache(
        clientId,
      );
      setReportTypeSetting(reportTypeSettingResult);
    };

    handleGetReportTypeSetting();
  }, [clientId]);

  const reportSettingChangeHandler = useCallback(
    (updatedReportSettingState: ReportSettingState) => {
      // temp unsavedChanges
      setSettingsHaveUnsavedChanges(
        !objectsAreEqual(initialSettingsToCompare, updatedReportSettingState),
      );

      setReportSettingState(updatedReportSettingState);
    },
    [setReportSettingState, initialSettingsToCompare],
  );

  const scheduleSettingChangeHandler = useCallback(
    (updatedReportSettingState: ReportSettingState) => {
      setScheduledReportSettingState(updatedReportSettingState);
    },
    [setScheduledReportSettingState],
  );

  // take a copy of current state to build schedule state
  useEffect(() => {
    setScheduledReportSettingState(reportSettingState);
  }, [reportSettingState, setScheduledReportSettingState]);

  const handleNotificationClose = (): void => {
    setEnableActionButtons(true);
    setIsShowNotification(false);
  };

  const scheduleRunClickHandler = useCallback(async () => {
    actionMenuRef.current?.scheduleReportAction('add');
  }, []);

  const handleCreateReportErrorNotificationClose = (): void => {
    handleNotificationClose();
    setIsShowCreateReportErrorNotification(false);
    resetError();
  };

  const runReportClickHandler = useCallback(async () => {
    setEnableActionButtons(false);
    resetError();

    const ranReportResult = await runStandardReport(
      client,
      (selectedSubCategory?.tag as StandardReport) ||
        (currentSelectedReport?.tag as StandardReport),
      reportSettingState,
      ruleBuilderFields,
      customCalendarSnapshot.isCustomCalendarEnabled,
      savedReport?.reportName || sharedReport?.reportName,
      clientSetting.isCoaEnabled,
    );
    if (ranReportResult) {
      setIsShowNotification(true);
      addNewInProgressReport(ranReportResult.id, ranReportResult.taskName);
    }

    const timer = setTimeout(() => {
      handleNotificationClose();
    }, 3000);
    return (): void => clearTimeout(timer);
  }, [
    client,
    selectedSubCategory?.tag,
    currentSelectedReport?.tag,
    reportSettingState,
    ruleBuilderFields,
    customCalendarSnapshot?.isCustomCalendarEnabled,
    savedReport?.reportName,
    sharedReport?.reportName,
    addNewInProgressReport,
    clientSetting.isCoaEnabled,
    resetError,
  ]);

  const addFieldsClickHandler = useCallback(() => {
    const hybridReportData: HybridReportData = {
      standardReport: currentSelectedReport?.tag as StandardReport,
      reportSettingState,
      ruleBuilderFields,
      reportTypeSetting,
      customCalendarSettings: customCalendarSnapshot,
    };

    // Store data in context instead of URL to avoid length limitations
    setHybridReportTransferData(hybridReportData);

    router.push(
      {
        pathname: '/create',
        query: {
          ishybridReportString: true,
          initialSavedReportId: savedReport?.id || 0,
        },
      },
      '/create',
    );
  }, [
    reportSettingState,
    reportTypeSetting,
    router,
    ruleBuilderFields,
    currentSelectedReport,
    customCalendarSnapshot,
    savedReport?.id,
    setHybridReportTransferData,
  ]);

  const standardReportIncludedFieldChangeHandler = useCallback(
    (fields: RuleBuilderField[]): void => {
      setRuleBuilderFields(fields);
    },
    [],
  );

  const hasEditingRuleBuilderField =
    checkIfHasEditingRuleBuilderField(ruleBuilderFields);

  const {
    reportType,
    reportId,
    isAllowPIT,
    isAllowSplitByAllocationForReport,
  } = (selectedSubCategory?.tag ||
    selectedNonStandardSubCategory?.tag ||
    {}) as StandardReport;

  const isRunReportDisabled =
    !isActionButtonsEnabled ||
    hasEditingRuleBuilderField ||
    !reportSettingState.isValid ||
    reportSettingState.pitIsInvalid;

  const isFuturePITDate =
    reportSettingState.pitErrors?.includes(
      'Please enter a non-future date or instead schedule a run in the future.',
    ) || reportSettingState.pitErrors?.length === 0;

  const isScheduleReportDisabled =
    !reportSettingState.isValid ||
    (reportSettingState.pitSwitchValue?.value && !isFuturePITDate);

  const subItemListBuilder = useCallback(
    (
      category: SideNavCategory,
      defaultItemComponents: StringOrComponent[],
    ): StringOrComponent => {
      return category.items.length ? (
        <>{defaultItemComponents}</>
      ) : (
        <NoItemsToDisplay colorScheme="blue" subCategory={category} />
      );
    },
    [],
  );

  const subItemDisplayBuilder = useCallback(
    (category: SideNavCategory<SavedReport>) => {
      const isShared = !!category.tag?.sharedNumber;
      const isScheduled = !!category.tag?.scheduleNumber;
      const name = replaceIfrsLabels(category.name, isFrs102Enabled);

      return (
        <Styled.ReportItem>
          {name}
          <div>
            {isShared && (
              <Icon name="share" width="16" height="16" color="#ffffff" />
            )}
            {isScheduled && (
              <Icon name="schedule" width="16" height="16" color="#ffffff" />
            )}
          </div>
        </Styled.ReportItem>
      );
    },
    [isFrs102Enabled],
  );

  const handleRestartReport = useCallback(() => {
    setReportSettingState(defaultReportSettingState);
    setReportHasUnsavedChanges(false);
    setFieldsHaveUnsavedChanges(false);
    setSettingsHaveUnsavedChanges(false);
  }, [defaultReportSettingState, setReportSettingState]);

  const { savedReportId } = router.query;

  return (
    <LeftSideNavContainer>
      <SideNav
        isLoading={isLoadingCategories}
        categories={categories}
        selectedCategory={selectedNonStandardCategory || selectedCategory}
        selectedSubCategory={currentSelectedReport}
        onCategorySelect={categorySelectHandler}
        onSubCategorySelect={subCategorySelectHandler}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        categoriesToSearch={searchableCategories}
        subItemListBuilder={subItemListBuilder}
        subItemDisplayBuilder={subItemDisplayBuilder}
        refreshFieldsCallback={() => undefined}
        canShowRefresh={false}
        onCloseRefreshPanel={() => undefined}
      />
      <GStyled.ScrollableContainer>
        {currentSelectedReport && (
          <ActionMenu
            ref={actionMenuRef}
            isSaveEnabled={!isRunReportDisabled}
            reportSettingState={reportSettingState}
            fields={ruleBuilderFields}
            isHybridReport={true}
            standardReport={
              (currentSelectedReport as SideNavCategory<StandardReport>).tag
            }
            triggerRestart={handleRestartReport}
            initialSavedReportId={0}
            reportMetadata={reportMetadata}
            isSaveAsNew={true}
            autoSubcategorySelect={autoSubcategorySelectHandler}
            reportHasUnsavedChanges={reportHasUnsavedChanges}
            resetUnsavedState={resetUnsavedState}
            savedReportDetail={savedReport}
            triggerReloadMyReports={updateMyReports}
          >
            <ReportDateSetting
              reportType={reportType as ReportType}
              isAllowPIT={isAllowPIT}
              isAllowSplitByAllocationForReport={
                isAllowSplitByAllocationForReport
              }
              onChange={scheduleSettingChangeHandler}
              reportSettingState={scheduledReportSettingState}
              reportTypeSetting={reportTypeSetting}
              customCalendarSettings={customCalendarSnapshot}
            />
          </ActionMenu>
        )}
        <Styled.FindReportContainer>
          {!currentSelectedReport && (
            <ReportInfo
              category={selectedCategory}
              subCategory={
                selectedSubCategory as SideNavCategory<StandardReport>
              }
            />
          )}
          {currentSelectedReport && (
            <>
              <ReportSetting
                reportType={reportType as ReportType}
                isInforReport={isInforReport}
                isAllowPIT={isAllowPIT}
                onChange={reportSettingChangeHandler}
                reportSettingState={reportSettingState}
                reportTypeSetting={reportTypeSetting}
                customCalendarSettings={customCalendarSnapshot}
              />
              <StandardReportIncludedFields
                reportSnapshot={reportSnapshot}
                standardReportId={reportId}
                savedReportDetail={savedReport}
                sharedReportDetail={sharedReport}
                onChange={standardReportIncludedFieldChangeHandler}
                onAddFieldsButtonCLick={addFieldsClickHandler}
                reportHasUnsavedChanges={(val: boolean): void =>
                  setFieldsHaveUnsavedChanges(val)
                }
              />

              <ReportDateSetting
                reportType={reportType as ReportType}
                isAllowPIT={isAllowPIT}
                isAllowSplitByAllocationForReport={
                  isAllowSplitByAllocationForReport
                }
                onChange={reportSettingChangeHandler}
                reportSettingState={reportSettingState}
                reportTypeSetting={reportTypeSetting}
                customCalendarSettings={customCalendarSnapshot}
              >
                <ButtonsBar>
                  <Button
                    buttonType="secondary"
                    onClick={scheduleRunClickHandler}
                    disabled={isScheduleReportDisabled}
                  >
                    <Icon name="schedule" disabled={isScheduleReportDisabled} />
                    <span>Schedule Run</span>
                  </Button>
                  <Button
                    buttonType="primary"
                    onClick={runReportClickHandler}
                    disabled={isRunReportDisabled}
                  >
                    Run Report
                  </Button>
                </ButtonsBar>
              </ReportDateSetting>
            </>
          )}
        </Styled.FindReportContainer>
      </GStyled.ScrollableContainer>
      <Dialog
        isOpen={isShowNotification}
        onStateChange={noop}
        withoutCover={true}
      >
        <ReportNotifications
          notificationType="running"
          onClose={handleNotificationClose}
        />
      </Dialog>

      <Dialog
        isOpen={isShowCreateReportErrorNotification}
        onStateChange={noop}
        withoutCover={true}
        maxWidth="60rem"
      >
        <ReportNotifications
          notificationType="createReportError"
          onClose={handleCreateReportErrorNotificationClose}
          errorMessage={runReportErrorMessage}
        />
      </Dialog>
      {/* save and continue flow - empty cover while schedule modal loads and navigate to saved report */}
      <Dialog
        emptyCover={true}
        isOpen={shouldSaveAndContinue && !!savedReportId}
      />
    </LeftSideNavContainer>
  );
}

export default withClient(FindReport);
